// ============================================================================
// ClientAppShell — the authenticated PORTAL frame (mobile-first PWA).
//   • a slim top bar: brand mark + language toggle + sign-out
//   • a content well sized for a phone, growing to a comfortable column on lg
//   • a sticky BOTTOM tab bar (Home · Log · Report) — the native-app pattern a
//     "Add to Home Screen" user expects, with safe-area inset padding
// Registers its own i18n namespace ("clientPortal") on the already-initialised
// instance, mirroring how the trainer AppShell owns "appShell". Renders nested
// routes via <Outlet/>.
// ============================================================================
import { Link, NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useClientAuth } from "./auth/ClientAuthContext";
import { useClientLiveSync } from "./useClientLiveSync";
import { useClientMessagesUnread } from "./hooks";
import { ClientLanguageToggle } from "./ClientLanguageToggle";
import {
  CLIENT_ACCOUNT_PATH,
  CLIENT_CALENDAR_PATH,
  CLIENT_HOME_PATH,
  CLIENT_LOG_PATH,
  CLIENT_MESSAGES_PATH,
  CLIENT_REPORT_PATH,
} from "./routes";
import { cn } from "../lib/cn";
import i18n from "../i18n/config";
import enClientPortal from "../i18n/locales/en/clientPortal.json";
import zhClientPortal from "../i18n/locales/zh/clientPortal.json";

// Own the clientPortal namespace (idempotent under HMR).
i18n.addResourceBundle("en", "clientPortal", enClientPortal, false, true);
i18n.addResourceBundle("zh", "clientPortal", zhClientPortal, false, true);

function BrandMark() {
  const { t } = useTranslation("clientPortal");
  return (
    <Link to={CLIENT_HOME_PATH} className="inline-flex items-center gap-2">
      <span className="grid h-8 w-8 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-base font-extrabold tracking-tightest text-body">
        {t("app.name")}
      </span>
    </Link>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path d="M4 11l8-7 8 7M6 10v9h12v-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function LogIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}
function ReportIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path d="M5 19V5h10l4 4v10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M8 13l2.5 2.5L16 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M3.5 9.5h17M8 3v3M16 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
        stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2.5v2M12 19.5v2M21.5 12h-2M4.5 12h-2M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4M18.7 18.7l-1.4-1.4M6.7 6.7 5.3 5.3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ClientAppShell() {
  const { t } = useTranslation("clientPortal");
  const { t: tm } = useTranslation("messages");
  const { logout, isAuthenticated } = useClientAuth();

  // Open the CLIENT-scoped live-sync WebSocket for the whole authenticated
  // portal session, mirroring how the trainer AppShell mounts useLiveSync. A
  // trainer message/read receipt then updates the thread + the badge below
  // with no reload.
  useClientLiveSync(isAuthenticated);

  // The bottom-nav Messages badge: unread (trainer→client) message count, kept
  // fresh by the `message` live-sync invalidation (no polling).
  const unread = useClientMessagesUnread();
  const unreadCount = unread.data?.unread ?? 0;

  const tabs = [
    { to: CLIENT_HOME_PATH, label: t("nav.home"), icon: <HomeIcon />, end: true, badge: 0 },
    { to: CLIENT_LOG_PATH, label: t("nav.log"), icon: <LogIcon />, end: false, badge: 0 },
    { to: CLIENT_MESSAGES_PATH, label: tm("tab"), icon: <MessageIcon />, end: false, badge: unreadCount },
    { to: CLIENT_CALENDAR_PATH, label: t("nav.calendar"), icon: <CalendarIcon />, end: false, badge: 0 },
    { to: CLIENT_REPORT_PATH, label: t("nav.report"), icon: <ReportIcon />, end: false, badge: 0 },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-paper text-body">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-line bg-surface/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
          <BrandMark />
          <div className="flex items-center gap-2">
            <ClientLanguageToggle />
            <NavLink
              to={CLIENT_ACCOUNT_PATH}
              aria-label={t("account.title")}
              className={({ isActive }) =>
                cn(
                  "grid h-9 w-9 place-items-center rounded-pill transition-colors",
                  isActive
                    ? "bg-coral-soft text-coral"
                    : "text-body-soft hover:bg-surface-sunken hover:text-body",
                )
              }
            >
              <SettingsIcon />
            </NavLink>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-pill px-3 py-1.5 font-display text-xs font-bold text-body-soft transition-colors hover:bg-surface-sunken hover:text-body"
            >
              {t("nav.logOut")}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-lg flex-1 px-4 pb-28 pt-5">
        <Outlet />
      </main>

      {/* Bottom tab bar — the native-app pattern for a home-screen PWA. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur-md"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label={t("app.name")}
      >
        <div className="mx-auto grid max-w-lg grid-cols-5">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-1 py-2.5 font-display text-[0.7rem] font-bold transition-colors",
                  isActive ? "text-coral" : "text-body-faint hover:text-body-soft",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "relative grid h-9 w-14 place-items-center rounded-pill transition-colors",
                      isActive && "bg-coral-soft",
                    )}
                  >
                    {tab.icon}
                    {tab.badge > 0 && (
                      <span
                        className="absolute right-2.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-pill bg-grad-coral px-1 text-[0.6rem] font-extrabold leading-none text-white shadow-volt"
                        aria-label={tm(tab.badge === 1 ? "unreadOne" : "unreadOther", { count: tab.badge })}
                      >
                        {tab.badge > 99 ? "99+" : tab.badge}
                      </span>
                    )}
                  </span>
                  {tab.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
